import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchEvents,
  createEventPrice,
  removeEventPrice,
  removeEvent,
  uploadNewEventImg,
  fetchanEvents,
  updateEvent,
} from "../../../controller/c_events.js";

import EditsEventModal from "./EditEventModal";
import PaymentModal from "./PaymentModal";
import UpdateEventImageModal from "./UpdateEventImageModal";

import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { getCurrencySign,buildDateText, formatAmount } from "../../../controller/helper.js";

const INITIAL_PAYMENT = { eventid: "", title: "", country: "", amount: 0.0 };
const INITIAL_EDIT_FORM = {
  id: "",
  title: "",
  startdate: "",
  enddate: "",
  duration: "",
  delivery: "",
  isfree: "",
  details: "",
};

const INITIAL_IMG_FORM = { id: "", title: "", image: "" };
export default function EventsListTable() {
    const token= useSelector((state)=>state.auth.login.token);
  const MySwal = useMemo(() => withReactContent(Swal), []);
  const user = useSelector((state) => state.auth.login);

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modals
  const [paymentopen, setPaymentOpen] = useState(false);
  const [editopen, setEditOpen] = useState(false);
  const [updateimgopen, setUpdateImgOpen] = useState(false);

  // Forms + errors
  const [paymentdata, setPaymentData] = useState(INITIAL_PAYMENT);
  const [editEventForm, setEditEventForm] = useState(INITIAL_EDIT_FORM);
  const [updateEventimgForm, setUpdateEventimgForm] = useState(INITIAL_IMG_FORM);

  const [editerrors, setEditErrors] = useState({});
  const [updateimgerrors, setUpdateImgErrors] = useState({});

  // -----------------------------
  // Data
  // -----------------------------
  const loadEvents = useCallback(async () => {
    try {
      const evts = await fetchEvents(token);
      setEvents(evts || []);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    setLoading(true);
    loadEvents();

    const id = setInterval(loadEvents, 10000);
    return () => clearInterval(id);
  }, [loadEvents]);

  // -----------------------------
  // SweetAlert helpers
  // -----------------------------
  const confirmDanger = useCallback(
    async ({ title, text }) => {
      const result = await MySwal.fire({
        title,
        text,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it",
        cancelButtonText: "Cancel",
        reverseButtons: true,
        focusCancel: true,
      });

      return result.isConfirmed;
    },
    [MySwal]
  );

  const toastOrError = (resp, fallback) => {
    toast.error(resp?.message || fallback);
  };

  // -----------------------------
  // Open modals
  // -----------------------------
  const openEditModal = useCallback(async (id) => {
    const event = await fetchanEvents(id);
    if (!event) return;

    setEditEventForm({
      id: event.eventid,
      title: event.etitle,
      startdate: event.event_startdate,
      enddate: event.event_enddate,
      duration: event.event_timeduration,
      delivery: event.deliverymode,
      isfree: event.isfree,
      details: event.details,
    });

    setEditErrors({});
    setEditOpen(true);
  }, []);

  const openPaymentModal = useCallback((evt) => {
    setPaymentData({
      eventid: evt.eventid,
      title: evt.etitle,
      country: "",
      amount: 0.0,
    });
    setPaymentOpen(true);
  }, []);

  const openUpdateImgModal = useCallback(async (id) => {
    const event = await fetchanEvents(id);
    if (!event) return;

    setUpdateEventimgForm({
      id: event.eventid,
      title: event.etitle,
      image: "",
    });

    setUpdateImgErrors({});
    setUpdateImgOpen(true);
  }, []);

  // -----------------------------
  // Close modals
  // -----------------------------
  const closeEditModal = useCallback(() => {
    setEditEventForm(INITIAL_EDIT_FORM);
    setEditOpen(false);
  }, []);

  const closePaymentModal = useCallback(() => {
    setPaymentData(INITIAL_PAYMENT);
    setPaymentOpen(false);
  }, []);

  const closeUpdateImgModal = useCallback(() => {
    setUpdateEventimgForm(INITIAL_IMG_FORM);
    setUpdateImgOpen(false);
  }, []);

  // -----------------------------
  // Submit handlers
  // -----------------------------
  const submitPaymentModal = useCallback(async () => {
    try {
      if (!paymentdata.country) return toast.error("Please select a country.");

      const amount = parseFloat(paymentdata.amount);
      if (Number.isNaN(amount) || amount <= 0) {
        return toast.error("Please enter a valid amount.");
      }

      const payload = { ...paymentdata, amount };
      const response = await createEventPrice(payload, user.userid);

      if (response && response.state) {
        toast.success(response.message || "Event cost added successfully.");
        closePaymentModal();
        loadEvents();
        return;
      }

      toastOrError(response, "Failed to add event cost.");
    } catch (err) {
      console.error("createEventPrice error", err);
      toast.error(err?.message || "Failed to add event cost.");
    }
  }, [paymentdata, user?.userid, closePaymentModal, loadEvents]);

  const submitEditModal = useCallback(async () => {
    const response = await updateEvent(editEventForm);

    if (response?.state) {
      toast.success(response.message || "Event updated successfully.");
      closeEditModal();
      loadEvents();
      return;
    }

    toastOrError(response, "Failed to update event.");
  }, [editEventForm, closeEditModal, loadEvents]);

  const submitUpdateImgModal = useCallback(async () => {
    const fd = new FormData();
    fd.append("data", JSON.stringify(updateEventimgForm));
    Object.entries(updateEventimgForm).forEach(([key, value]) => {
      fd.append(key, value);
    });

    const response = await uploadNewEventImg(fd, updateEventimgForm.id);

    if (response?.state) {
      toast.success(response.message || "Event image updated successfully.");
      closeUpdateImgModal();
      loadEvents();
      return;
    }

    toastOrError(response, "Failed to update event image.");
  }, [updateEventimgForm, closeUpdateImgModal, loadEvents]);

  // -----------------------------
  // Delete handlers
  // -----------------------------
  const deleteEvent = useCallback(
    async (eventid, eventtitle) => {
      const ok = await confirmDanger({
        title: `Are you sure you want to delete "${eventtitle}"?`,
        text: "This action cannot be undone.",
      });
      if (!ok) return;

      const response = await removeEvent(eventid);

      if (!response?.state) return toastOrError(response, "Failed to delete item.");

      if (response.state === "success") {
        await MySwal.fire({
          icon: "success",
          title: response.message || "Deleted!",
          text: "The event has been removed.",
          timer: 1400,
          showConfirmButton: false,
        });
      } else {
        toastOrError(response, "Could not delete event.");
      }

      setEvents((prev) => prev.filter((e) => e.eventid !== eventid));
    },
    [confirmDanger, MySwal]
  );

  const deleteEventPrice = useCallback(
    (pid, eventid) => async () => {
      const ok = await confirmDanger({
        title: "Are you sure you want to delete this price?",
        text: "This action cannot be undone.",
      });
      if (!ok) return;

      const response = await removeEventPrice(pid, eventid);

      if (!response?.state) return toastOrError(response, "Failed to delete item.");

      if (response.state === "success") {
        await MySwal.fire({
          icon: "success",
          title: response.message || "Deleted!",
          text: "The event price has been removed.",
          timer: 1400,
          showConfirmButton: false,
        });
        loadEvents();
      } else {
        toastOrError(response, "Could not delete event price.");
      }
    },
    [confirmDanger, MySwal, loadEvents]
  );

  // -----------------------------
  // Render helpers
  // -----------------------------
  const renderPrices = (evt) => {
    if (!evt?.price?.length) return null;

    return evt.price.map((p, i) => (
      <div key={i} className="text-white-50 small">
        <i
          onClick={deleteEventPrice(p.pid, evt.eventid)}
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          title="Delete country event amount"
          style={{ color: "red", cursor: "pointer" }}
          className="bi bi-trash"
        />
        <span className="text-white-50"> {p.country}:</span>{" "}
        {getCurrencySign(p.country)} {formatAmount(p.price) || ""}
      </div>
    ));
  };

  const renderVisibilityBadge = (visibilitystatus) => {
    const cls =
      visibilitystatus === 0
        ? "text-bg-success"
        : visibilitystatus === 1
        ? "text-bg-secondary"
        : "text-bg-warning";

    return (
      <span className={`badge ${cls}`}>
        {visibilitystatus === 0 ? (
          <i className="bi bi-eye" />
        ) : (
          <i className="bi bi-eye-slash" />
        )}
      </span>
    );
  };

  // -----------------------------
  // UI
  // -----------------------------
  return (
    <div className="card admin-card">
      <div className="card-body">
        <div className="table-responsive">
          <table className="table table-dark table-hover align-middle">
            <thead>
              <tr>
                <th>Event</th>
                <th>Dates and Time</th>
                <th>Delivery Mode</th>
                <th>Status</th>
                <th>No. Reg.</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center text-white-50 py-4">
                    Loading events...
                  </td>
                </tr>
              ) : events.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-white-50 py-4">
                    No events added yet. Click <b>New Event</b> to create one.
                  </td>
                </tr>
              ) : (
                events.map((evt) => (
                  <React.Fragment key={evt.eventid}>
                    <tr>
                      <td>
                        <div
                          onClick={() => {
                            alert("dhdhdh");
                          }}
                          className="fw-bold"
                        >
                          {evt.etitle}
                        </div>

                        {renderPrices(evt)}
                      </td>

                      <td className="small">
                        <div>
                          
                          {buildDateText(evt.event_startdate, evt.event_enddate) || ""}
                        </div>
                        <div>
                          <span className="text-white-50">Time:</span>{" "}
                          {evt.event_timeduration || "—"}
                        </div>
                      </td>

                      <td>{evt.deliverymode || ""}</td>

                      <td>{renderVisibilityBadge(evt.visibilitystatus)}</td>

                      <td>{evt.reg_counts}</td>

                      <td className="text-end">
                        <div className="btn-group">
                          {evt.isfree === 0 && (
                            <button
                              className="btn btn-sm btn-outline-warning"
                              data-bs-toggle="tooltip"
                              data-bs-placement="top"
                              title="Add Event Cost"
                              onClick={() => openPaymentModal(evt)}
                            >
                              <i className="bi bi-cash" />
                            </button>
                          )}

                          <button
                            className="btn btn-sm btn-outline-info"
                            onClick={() => openEditModal(evt.eventid)}
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            title="Edit Event"
                          >
                            <i className="bi bi-pencil" />
                          </button>

                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => deleteEvent(evt.eventid, evt.etitle)}
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            title="Delete Event"
                          >
                            <i className="bi bi-trash" />
                          </button>

                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => openUpdateImgModal(evt.eventid)}
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            title="Change Event Image"
                          >
                            <i className="bi bi-image" />
                          </button>
                        </div>
                      </td>
                    </tr>

                    <tr>
                      <td colSpan={6} className="p-0">
                        <div className="bg-dark text-white bg-opacity-25 p-3 small">
                          {evt.details}
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* manage edit event modal */}
      <EditsEventModal
        open={editopen}
        form={editEventForm}
        setForm={setEditEventForm}
        errors={editerrors}
        onClose={closeEditModal}
        onSubmit={submitEditModal}
      />
{/* manage update event image modal */}
      <UpdateEventImageModal
        open={updateimgopen}
        form={updateEventimgForm}
        setForm={setUpdateEventimgForm}
        errors={updateimgerrors}
        onClose={closeUpdateImgModal}
        onSubmit={submitUpdateImgModal}
      />

{/* manage event payment modal */}
      <PaymentModal
        open={paymentopen}
        form={paymentdata}
        setForm={setPaymentData}
        onClose={closePaymentModal}
        onSubmit={submitPaymentModal}
      />
    </div>
  );
}
